import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../utils/api';
import Layout from '../components/Layout';
import TipTapEditor from '../components/TipTapEditor';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Calendar as CalendarIcon, Plus, Edit, Trash2, MapPin, Users, ChevronLeft,
  ChevronRight, ClipboardList, UserX, Settings as SettingsIcon, Globe, Eye, EyeOff,
  Upload, List, Clock, Bell, CreditCard, RefreshCw, Search, Filter, Tag, Link2,
  CheckCircle2, AlertCircle, Loader2, Code, Copy, Check,
} from 'lucide-react';
import { toast } from 'sonner';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const GROUP_TYPES = [
  { value: 'class', label: 'Class' },
  { value: 'year', label: 'Year Group' },
  { value: 'family', label: 'Family' },
  { value: 'custom', label: 'Custom' },
];
const COLORS = [
  // Reds & pinks
  '#ef4444','#dc2626','#f87171','#ec4899','#db2777','#f472b6','#e879f9',
  // Oranges & ambers
  '#f97316','#ea580c','#fb923c','#f59e0b','#d97706','#fbbf24',
  // Yellows & limes
  '#eab308','#84cc16','#65a30d',
  // Greens
  '#22c55e','#16a34a','#10b981','#059669',
  // Teals & cyans
  '#14b8a6','#0891b2','#06b6d4',
  // Blues
  '#60a5fa','#3b82f6','#2563eb','#1d4ed8',
  // Indigos & purples
  '#6366f1','#4f46e5','#7c3aed','#8b5cf6','#a855f7',
  // Neutrals & slates
  '#64748b','#475569','#78716c',
];
const REMINDER_PRESETS = [
  { minutes: 10080, label: '1 week before' },
  { minutes: 4320, label: '3 days before' },
  { minutes: 1440, label: '1 day before' },
  { minutes: 120, label: '2 hours before' },
];
const PRESET_MINUTES = REMINDER_PRESETS.map(p => p.minutes);

const emptyEvent = () => ({
  title: '',
  description_html: '',
  category: '',
  color: '#6366f1',
  start: '',
  end: '',
  all_day: false,
  location: '',
  cost: '',
  capacity: '',
  form_id: '',
  external_link_url: '',
  audience: { whole_school: true, group_ids: [] },
  status: 'draft',
  rsvp_enabled: false,
  send_push: false,
  waitlist_enabled: false,
  waitlist_claim_hours: 24,
  waitlist_max: '',
  reminderPresets: [],
  customReminderValue: '',
  customReminderUnit: 'days',
  reminder_extra_recipients: '',
  // Classification & filtering
  event_type: '',
  departments: [],
  tags: [],
  // Ticketing
  ticket_enabled: false,
  ticket_price: '',
  ticket_currency: 'usd',
  ticket_name: 'General Admission',
  ticket_limit_per_order: 10,
});

const fmtDateTimeLocal = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    const local = new Date(d.getTime() - off * 60000);
    return local.toISOString().slice(0, 16);
  } catch { return ''; }
};
const localToIso = (val) => {
  if (!val) return null;
  try { return new Date(val).toISOString(); } catch { return null; }
};


/** Masks the secret token in a private iCal URL, with reveal + copy toggles. */
const IcalUrlMasked = ({ url }) => {
  const [revealed, setRevealed] = React.useState(false);
  const masked = React.useMemo(() => {
    try {
      const u = new URL(url);
      const parts = u.pathname.split('/');
      const privIdx = parts.indexOf('private');
      if (privIdx !== -1 && parts[privIdx + 1]) {
        const tok = parts[privIdx + 1];
        parts[privIdx + 1] = tok.slice(0, 4) + '••••••••' + tok.slice(-4);
        return u.hostname + parts.join('/');
      }
      // Non-Google: show domain + masked path
      return u.hostname + '/…/••••••••.ics';
    } catch { return '••••••••••••••••'; }
  }, [url]);

  return (
    <div className="flex items-center gap-1.5 mt-0.5">
      <p className="text-xs text-slate-400 truncate font-mono">{revealed ? url : masked}</p>
      <button onClick={() => setRevealed(r => !r)}
        className="shrink-0 text-slate-300 hover:text-slate-500 transition-colors"
        title={revealed ? 'Hide URL' : 'Reveal URL'}>
        {revealed ? <EyeOff size={12} /> : <Eye size={12} />}
      </button>
      <button onClick={() => { navigator.clipboard.writeText(url); }}
        className="shrink-0 text-slate-300 hover:text-slate-500 transition-colors"
        title="Copy URL">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
        </svg>
      </button>
    </div>
  );
};


const Events = () => {
  const [tab, setTab] = useState('calendar');
  const [events, setEvents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [forms, setForms] = useState([]);
  const [absentees, setAbsentees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteSlug, setSiteSlug] = useState('');
  const [taptileConnected, setTaptileConnected] = useState(false);

  // Event dialog
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState(emptyEvent());
  const [saving, setSaving] = useState(false);

  // Calendar navigation
  const [viewDate, setViewDate] = useState(new Date());

  // Group dialog
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: '', type: 'custom', member_emails: '', taptile_group_id: '' });

  // CSV import
  const [importOpen, setImportOpen] = useState(false);
  const [importForm, setImportForm] = useState({ name: '', type: 'custom', csv_text: '' });

  // Settings
  const [settings, setSettings] = useState({ office_email: '', absentee_auto_responder: '', absentee_enabled: true, api_auto_publish: false });

  // RSVP viewer
  const [rsvpDialogOpen, setRsvpDialogOpen] = useState(false);
  const [rsvpEvent, setRsvpEvent] = useState(null);
  const [rsvpData, setRsvpData] = useState(null);

  // Tickets tab
  const [ticketEventId, setTicketEventId] = useState('');
  const [ticketsData, setTicketsData]     = useState(null);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [refunding, setRefunding]   = useState(null);
  const [checkingIn, setCheckingIn] = useState(null);

  // Stripe Connect status (for event editor warning)
  const [stripeConnectStatus, setStripeConnectStatus] = useState(null);

  // Filter options (for the event dialog autocomplete)
  const [filterOptions, setFilterOptions] = useState({ event_types: [], departments: [], tags: [] });
  // Tag input helpers
  const [deptInput, setDeptInput] = useState('');
  const [tagInput, setTagInput] = useState('');

  // iCal Sources (Task 2)
  const [icalSources, setIcalSources] = useState([]);
  const [icalForm, setIcalForm] = useState({ name: '', url: '', color: '#6366f1', sync_interval_hours: 6 });
  const [icalFormOpen, setIcalFormOpen] = useState(false);
  const [editingIcal, setEditingIcal] = useState(null);
  const [icalSyncing, setIcalSyncing] = useState(null);

  // Admin calendar filter bar (Task 3)
  const [adminFilterType, setAdminFilterType] = useState('');
  const [adminFilterDept, setAdminFilterDept] = useState('');
  const [adminFilterSearch, setAdminFilterSearch] = useState('');
  const [adminFilterSource, setAdminFilterSource] = useState('');

  // Embed snippet builder state
  const [embedView, setEmbedView] = useState('calendar');
  const [embedShowFilters, setEmbedShowFilters] = useState(true);
  const [embedShowIcal, setEmbedShowIcal] = useState(true);
  const [embedShowAbsence, setEmbedShowAbsence] = useState(true);
  const [embedHeight, setEmbedHeight] = useState('700');
  const [embedCopied, setEmbedCopied] = useState(false);

  // Manage Labels — add inputs
  const [newTypeInput, setNewTypeInput] = useState('');
  const [newDeptInput, setNewDeptInput] = useState('');
  const [newTagInput, setNewTagInput] = useState('');

  const openRsvps = async (ev) => {
    setRsvpEvent(ev);
    setRsvpData(null);
    setRsvpDialogOpen(true);
    try {
      const res = await api.get(`/events/${ev.event_id}/rsvps`);
      setRsvpData(res.data);
    } catch { toast.error('Failed to load RSVPs'); }
  };

  const removeRsvp = async (rsvpId) => {
    try {
      await api.delete(`/events/${rsvpEvent.event_id}/rsvps/${rsvpId}`);
      const res = await api.get(`/events/${rsvpEvent.event_id}/rsvps`);
      setRsvpData(res.data);
      toast.success('RSVP removed');
    } catch { toast.error('Failed to remove'); }
  };

  const removeWaitlist = async (waitlistId) => {
    try {
      await api.delete(`/events/${rsvpEvent.event_id}/waitlist/${waitlistId}`);
      const res = await api.get(`/events/${rsvpEvent.event_id}/rsvps`);
      setRsvpData(res.data);
      toast.success('Waitlist entry removed');
    } catch { toast.error('Failed to remove'); }
  };

  const togglePreset = (minutes) => {
    setEventForm(f => {
      const has = f.reminderPresets.includes(minutes);
      return { ...f, reminderPresets: has ? f.reminderPresets.filter(m => m !== minutes) : [...f.reminderPresets, minutes] };
    });
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [evRes, grRes, fmRes] = await Promise.all([
        api.get('/events'),
        api.get('/events/groups'),
        api.get('/forms'),
      ]);
      setEvents(evRes.data || []);
      setGroups(grRes.data || []);
      setForms(fmRes.data || []);
      api.get('/events/taptile-status').then(r => setTaptileConnected(!!r.data?.connected)).catch(() => {});
      api.get('/events/filter-options').then(r => setFilterOptions(r.data || { event_types: [], departments: [], tags: [] })).catch(() => {});
      api.get('/stripe-connect/status').then(r => setStripeConnectStatus(r.data)).catch(() => {});
      // Resolve site slug for the Embed tab — non-critical, never blocks events loading
      api.get('/auth/me').then(async meRes => {
        const tid = meRes.data?.tenant_id;
        if (tid) {
          try {
            const siteRes = await api.get(`/public/events/${tid}`);
            setSiteSlug(siteRes.data?.site_slug || tid);
          } catch { setSiteSlug(tid); }
        }
      }).catch(() => {});
    } catch (e) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const loadAbsentees = useCallback(async () => {
    try {
      const res = await api.get('/events/absentees');
      setAbsentees(res.data || []);
    } catch { /* ignore */ }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const res = await api.get('/events/settings');
      setSettings({
        office_email: res.data?.office_email || '',
        absentee_auto_responder: res.data?.absentee_auto_responder || '',
        absentee_enabled: res.data?.absentee_enabled !== false,
        api_auto_publish: !!res.data?.api_auto_publish,
      });
    } catch { /* ignore */ }
  }, []);

  const loadIcalSources = useCallback(async () => {
    try {
      const res = await api.get('/events/ical-sources');
      setIcalSources(res.data || []);
    } catch { /* ignore */ }
  }, []);

  const refreshFilterOptions = useCallback(async () => {
    try {
      const res = await api.get('/events/filter-options');
      setFilterOptions(res.data || { event_types: [], departments: [], tags: [] });
    } catch { /* ignore */ }
  }, []);

  const deleteLabel = async (field, value, count) => {
    if (!window.confirm(`Remove "${value}" from all ${count} event(s)? This cannot be undone.`)) return;
    try {
      await api.delete('/events/filter-options', { params: { field, value } });
      toast.success(`"${value}" removed`);
      await Promise.all([fetchAll(), refreshFilterOptions()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove label');
    }
  };

  const addLabel = async (field, value, clearFn) => {
    const v = value.trim();
    if (!v) return;
    try {
      await api.post('/events/filter-options', { field, value: v });
      toast.success(`"${v}" added`);
      clearFn('');
      await refreshFilterOptions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add label');
    }
  };

  const saveIcalSource = async () => {
    if (!icalForm.name.trim() || !icalForm.url.trim()) { toast.error('Name and URL are required'); return; }
    try {
      if (editingIcal) {
        await api.put(`/events/ical-sources/${editingIcal.source_id}`, icalForm);
        toast.success('Source updated');
      } else {
        await api.post('/events/ical-sources', icalForm);
        toast.success('iCal source added — syncing now…');
      }
      setIcalFormOpen(false);
      setEditingIcal(null);
      setIcalForm({ name: '', url: '', color: '#6366f1', sync_interval_hours: 6 });
      loadIcalSources();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save source');
    }
  };

  const deleteIcalSource = async (src) => {
    if (!window.confirm(`Remove "${src.name}" and delete all ${src.events_count || 0} imported event(s)?`)) return;
    try {
      await api.delete(`/events/ical-sources/${src.source_id}`);
      toast.success('Source removed');
      loadIcalSources();
      fetchAll();
    } catch { toast.error('Failed to remove source'); }
  };

  const syncNow = async (src) => {
    setIcalSyncing(src.source_id);
    try {
      const res = await api.post(`/events/ical-sources/${src.source_id}/sync`);
      const d = res.data;
      if (d.ok) toast.success(`Sync complete — ${d.added} added, ${d.updated} updated, ${d.removed} removed`);
      else toast.error(`Sync failed: ${d.error}`);
      loadIcalSources();
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setIcalSyncing(null);
    }
  };

  const loadTickets = useCallback(async (evId) => {
    if (!evId) { setTicketsData(null); return; }
    setTicketsLoading(true);
    try {
      const res = await api.get(`/event-payments/admin/${evId}/tickets`);
      setTicketsData(res.data);
    } catch {
      toast.error('Failed to load tickets');
      setTicketsData(null);
    } finally {
      setTicketsLoading(false);
    }
  }, []);

  const refundTicket = async (ticket) => {
    if (!window.confirm(`Issue a full refund to ${ticket.buyer_name} for ${ticket.quantity} ticket(s)? This cannot be undone.`)) return;
    setRefunding(ticket.ticket_id);
    try {
      await api.post(`/event-payments/admin/tickets/${ticket.ticket_id}/refund`, {});
      toast.success('Refund issued successfully');
      loadTickets(ticketEventId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refund failed');
    } finally {
      setRefunding(null);
    }
  };

  const toggleCheckin = async (ticket) => {
    setCheckingIn(ticket.ticket_id);
    try {
      await api.post(`/event-payments/admin/tickets/${ticket.ticket_id}/checkin`, {});
      toast.success(ticket.checked_in ? 'Check-in removed' : 'Checked in');
      loadTickets(ticketEventId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Check-in toggle failed');
    } finally {
      setCheckingIn(null);
    }
  };

  useEffect(() => {
    if (tab === 'absentees') loadAbsentees();
    if (tab === 'settings') { loadSettings(); refreshFilterOptions(); }
    if (tab === 'ical') loadIcalSources();
    if (tab === 'tickets') {
      const ticketed = events.filter(e => e.ticket_enabled);
      if (ticketed.length > 0) {
        const evId = ticketEventId || ticketed[0].event_id;
        setTicketEventId(evId);
        loadTickets(evId);
      }
    }
  }, [tab, loadAbsentees, loadSettings, loadTickets, loadIcalSources, refreshFilterOptions, events, ticketEventId]);

  // ---------- Event CRUD ----------
  const openNewEvent = (dateStr) => {
    const base = emptyEvent();
    if (dateStr) {
      base.start = `${dateStr}T09:00`;
      base.end = `${dateStr}T10:00`;
    }
    setDeptInput('');
    setTagInput('');
    setEventForm(base);
    setEditingEvent(null);
    setEventDialogOpen(true);
  };

  const openEditEvent = (ev) => {
    const reminders = ev.reminders || [];
    const presetMins = reminders.map(r => r.offset_minutes).filter(m => PRESET_MINUTES.includes(m));
    const custom = reminders.find(r => !PRESET_MINUTES.includes(r.offset_minutes));
    let customValue = '', customUnit = 'days';
    if (custom) {
      if (custom.offset_minutes % 1440 === 0) { customValue = String(custom.offset_minutes / 1440); customUnit = 'days'; }
      else { customValue = String(Math.round(custom.offset_minutes / 60)); customUnit = 'hours'; }
    }
    setDeptInput('');
    setTagInput('');
    setEventForm({
      title: ev.title || '',
      description_html: ev.description_html || '',
      category: ev.category || '',
      color: ev.color || '#6366f1',
      start: fmtDateTimeLocal(ev.start),
      end: fmtDateTimeLocal(ev.end),
      all_day: !!ev.all_day,
      location: ev.location || '',
      cost: ev.cost ?? '',
      capacity: ev.capacity ?? '',
      form_id: ev.form_id || '',
      external_link_url: ev.external_link_url || '',
      audience: ev.audience || { whole_school: true, group_ids: [] },
      status: ev.status || 'draft',
      rsvp_enabled: !!ev.rsvp_enabled,
      send_push: !!ev.send_push,
      waitlist_enabled: !!ev.waitlist_enabled,
      waitlist_claim_hours: ev.waitlist_claim_hours ?? 24,
      waitlist_max: ev.waitlist_max ?? '',
      reminderPresets: presetMins,
      customReminderValue: customValue,
      customReminderUnit: customUnit,
      reminder_extra_recipients: (ev.reminder_extra_recipients || []).join('\n'),
      // Classification & filtering
      event_type: ev.event_type || '',
      departments: ev.departments || [],
      tags: ev.tags || [],
      // Ticketing
      ticket_enabled: !!ev.ticket_enabled,
      ticket_price: ev.ticket_price ?? '',
      ticket_currency: ev.ticket_currency || 'usd',
      ticket_name: ev.ticket_name || 'General Admission',
      ticket_limit_per_order: ev.ticket_limit_per_order ?? 10,
    });
    setEditingEvent(ev);
    setEventDialogOpen(true);
  };

  const buildReminders = (f) => {
    const list = f.reminderPresets.map(m => ({
      offset_minutes: m,
      label: REMINDER_PRESETS.find(p => p.minutes === m)?.label,
    }));
    if (f.customReminderValue && parseInt(f.customReminderValue, 10) > 0) {
      const n = parseInt(f.customReminderValue, 10);
      const minutes = f.customReminderUnit === 'hours' ? n * 60 : n * 1440;
      if (!PRESET_MINUTES.includes(minutes)) {
        list.push({ offset_minutes: minutes, label: `${n} ${f.customReminderUnit} before` });
      }
    }
    return list;
  };

  const saveEvent = async () => {
    if (!eventForm.title.trim()) { toast.error('Title is required'); return; }
    if (!eventForm.start) { toast.error('Start date/time is required'); return; }
    setSaving(true);
    const payload = {
      title: eventForm.title.trim(),
      description_html: eventForm.description_html,
      category: eventForm.category || null,
      color: eventForm.color,
      start: localToIso(eventForm.start),
      end: eventForm.end ? localToIso(eventForm.end) : null,
      all_day: eventForm.all_day,
      location: eventForm.location || null,
      cost: eventForm.cost === '' ? null : parseFloat(eventForm.cost),
      capacity: eventForm.capacity === '' ? null : parseInt(eventForm.capacity, 10),
      form_id: eventForm.form_id || null,
      external_link_url: eventForm.external_link_url?.trim() || null,
      audience: eventForm.audience,
      status: eventForm.status,
      rsvp_enabled: eventForm.rsvp_enabled,
      send_push: eventForm.send_push,
      waitlist_enabled: eventForm.rsvp_enabled && eventForm.waitlist_enabled,
      waitlist_claim_hours: parseInt(eventForm.waitlist_claim_hours, 10) || 24,
      waitlist_max: eventForm.waitlist_max === '' ? null : parseInt(eventForm.waitlist_max, 10),
      reminders: buildReminders(eventForm),
      reminder_extra_recipients: eventForm.reminder_extra_recipients.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean),
      // Classification & filtering
      event_type: eventForm.event_type || null,
      departments: eventForm.departments.filter(Boolean),
      tags: eventForm.tags.filter(Boolean),
      // Ticketing
      ticket_enabled: eventForm.ticket_enabled,
      ticket_price: eventForm.ticket_price === '' ? null : parseFloat(eventForm.ticket_price),
      ticket_currency: eventForm.ticket_currency,
      ticket_name: eventForm.ticket_name || 'General Admission',
      ticket_limit_per_order: parseInt(eventForm.ticket_limit_per_order, 10) || 10,
    };
    try {
      let resp;
      if (editingEvent) {
        resp = await api.put(`/events/${editingEvent.event_id}`, payload);
        toast.success('Event updated');
      } else {
        resp = await api.post('/events', payload);
        toast.success('Event created');
      }
      const push = resp?.data?.push;
      if (push) {
        if (push.ok) toast.success('Push sent to the Taptile app 🔔');
        else if (push.reason === 'taptile_not_configured') toast.info('Saved. Connect Taptile to send app push notifications.');
        else toast.error('Event saved, but the Taptile push failed to send.');
      }
      setEventDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const sendPushNow = async (ev) => {
    if (!window.confirm(`Send a push notification for "${ev.title}" to the Taptile app now?`)) return;
    try {
      const res = await api.post(`/events/${ev.event_id}/send-push`);
      const d = res.data?.result?.response?.delivery;
      const audience = res.data?.result?.response?.audience;
      const reached = audience?.resolved_users;
      toast.success(reached != null ? `Push sent to ~${reached} recipient(s) 🔔` : 'Push sent to Taptile 🔔');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send push');
    }
  };

  const deleteEvent = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try {
      await api.delete(`/events/${ev.event_id}`);
      toast.success('Event deleted');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const togglePublish = async (ev) => {
    try {
      if (ev.status === 'published') {
        await api.post(`/events/${ev.event_id}/unpublish`);
        toast.success('Moved to draft');
      } else {
        await api.post(`/events/${ev.event_id}/publish`);
        toast.success('Event published');
      }
      fetchAll();
    } catch { toast.error('Failed to update status'); }
  };

  // ---------- Group CRUD ----------
  const openNewGroup = () => {
    setGroupForm({ name: '', type: 'custom', member_emails: '', taptile_group_id: '' });
    setEditingGroup(null);
    setGroupDialogOpen(true);
  };
  const openEditGroup = (g) => {
    setGroupForm({ name: g.name, type: g.type, member_emails: (g.member_emails || []).join('\n'), taptile_group_id: g.taptile_group_id || '' });
    setEditingGroup(g);
    setGroupDialogOpen(true);
  };
  const saveGroup = async () => {
    if (!groupForm.name.trim()) { toast.error('Group name required'); return; }
    const emails = groupForm.member_emails.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);
    const payload = { name: groupForm.name.trim(), type: groupForm.type, member_emails: emails, taptile_group_id: groupForm.taptile_group_id.trim() || null };
    try {
      if (editingGroup) await api.put(`/events/groups/${editingGroup.group_id}`, payload);
      else await api.post('/events/groups', payload);
      toast.success('Group saved');
      setGroupDialogOpen(false);
      fetchAll();
    } catch { toast.error('Failed to save group'); }
  };
  const deleteGroup = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"?`)) return;
    try { await api.delete(`/events/groups/${g.group_id}`); toast.success('Group deleted'); fetchAll(); }
    catch { toast.error('Failed to delete'); }
  };

  const doImport = async () => {
    if (!importForm.csv_text.trim()) { toast.error('Paste some CSV data'); return; }
    try {
      const res = await api.post('/events/groups/import', importForm);
      toast.success(`Imported ${res.data.imported} members`);
      setImportOpen(false);
      setImportForm({ name: '', type: 'custom', csv_text: '' });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
  };

  const saveSettings = async () => {
    try {
      await api.put('/events/settings', settings);
      toast.success('Settings saved');
    } catch { toast.error('Failed to save settings'); }
  };

  // ---------- Calendar grid ----------
  // Admin filter (Task 3)
  const filteredAdminEvents = useMemo(() => {
    let evs = events;
    if (adminFilterType) evs = evs.filter(e => (e.event_type || '').toLowerCase() === adminFilterType.toLowerCase());
    if (adminFilterDept) evs = evs.filter(e => (e.departments || []).some(d => d.toLowerCase() === adminFilterDept.toLowerCase()));
    if (adminFilterSource) evs = evs.filter(e => e.ical_source_id === adminFilterSource);
    if (adminFilterSearch) {
      const s = adminFilterSearch.toLowerCase();
      evs = evs.filter(e =>
        (e.title || '').toLowerCase().includes(s) ||
        (e.location || '').toLowerCase().includes(s) ||
        (e.event_type || '').toLowerCase().includes(s) ||
        (e.departments || []).some(d => d.toLowerCase().includes(s))
      );
    }
    return evs;
  }, [events, adminFilterType, adminFilterDept, adminFilterSearch, adminFilterSource]);

  const calendarCells = useMemo(() => {    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const first = new Date(year, month, 1);
    const startDay = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [viewDate]);

  const eventsByDay = useMemo(() => {
    const map = {};
    filteredAdminEvents.forEach(ev => {
      if (!ev.start) return;
      const start = new Date(ev.start);
      const end = ev.end ? new Date(ev.end) : start;
      // Normalise to midnight for day comparison
      const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        const key = new Date(d).toDateString();
        (map[key] = map[key] || []).push(ev);
      }
    });
    return map;
  }, [filteredAdminEvents]);

  const monthLabel = viewDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const toDateInput = (d) => {
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
  };

  const toggleGroupInAudience = (gid) => {
    setEventForm(f => {
      const ids = f.audience.group_ids || [];
      const next = ids.includes(gid) ? ids.filter(x => x !== gid) : [...ids, gid];
      return { ...f, audience: { ...f.audience, group_ids: next } };
    });
  };

  const addDept = (val) => {
    const v = val.trim();
    if (!v) return;
    setEventForm(f => ({ ...f, departments: f.departments.includes(v) ? f.departments : [...f.departments, v] }));
    setDeptInput('');
  };

  const removeDept = (d) => setEventForm(f => ({ ...f, departments: f.departments.filter(x => x !== d) }));

  const addTag = (val) => {
    const v = val.trim();
    if (!v) return;
    setEventForm(f => ({ ...f, tags: f.tags.includes(v) ? f.tags : [...f.tags, v] }));
    setTagInput('');
  };

  const removeTag = (t) => setEventForm(f => ({ ...f, tags: f.tags.filter(x => x !== t) }));

  const publicCalUrl = siteSlug
    ? `${process.env.REACT_APP_BACKEND_URL}/api/public/events/${siteSlug}/calendar.ics`
    : '';
  const publicPageUrl = siteSlug ? `/site/${siteSlug}/events` : '';

  const embedSrc = siteSlug
    ? (() => {
        const origin = window.location.origin;
        const params = new URLSearchParams({ embed: '1', view: embedView });
        if (!embedShowFilters) params.set('show_filters', '0');
        if (!embedShowIcal) params.set('show_ical', '0');
        if (!embedShowAbsence) params.set('show_absence', '0');
        return `${origin}/site/${siteSlug}/events?${params.toString()}`;
      })()
    : '';

  const embedSnippet = embedSrc
    ? `<iframe\n  src="${embedSrc}"\n  width="100%"\n  height="${embedHeight}"\n  style="border:none;border-radius:12px;"\n  loading="lazy"\n  title="Events Calendar"\n></iframe>`
    : '';

  const copyEmbed = () => {
    if (!embedSnippet) return;
    navigator.clipboard.writeText(embedSnippet).then(() => {
      setEmbedCopied(true);
      setTimeout(() => setEmbedCopied(false), 2000);
    });
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-6" data-testid="events-page">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <CalendarIcon className="w-7 h-7 text-indigo-600" /> Events & Calendar
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              Create audience-targeted events, publish a calendar and share iCal feeds.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {publicPageUrl && (
              <a href={publicPageUrl} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" data-testid="events-view-public-btn">
                  <Globe className="w-4 h-4 mr-2" /> View public calendar
                </Button>
              </a>
            )}
            <Button onClick={() => openNewEvent()} data-testid="events-new-btn">
              <Plus className="w-4 h-4 mr-2" /> New Event
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="calendar" data-testid="events-tab-calendar"><CalendarIcon className="w-4 h-4 mr-1" /> Calendar</TabsTrigger>
            <TabsTrigger value="list" data-testid="events-tab-list"><List className="w-4 h-4 mr-1" /> List</TabsTrigger>
            <TabsTrigger value="groups" data-testid="events-tab-groups"><Users className="w-4 h-4 mr-1" /> Groups</TabsTrigger>
            <TabsTrigger value="tickets" data-testid="events-tab-tickets"><CreditCard className="w-4 h-4 mr-1" /> Tickets</TabsTrigger>
            <TabsTrigger value="absentees" data-testid="events-tab-absentees"><UserX className="w-4 h-4 mr-1" /> Absentees</TabsTrigger>
            <TabsTrigger value="ical" data-testid="events-tab-ical"><Link2 className="w-4 h-4 mr-1" /> iCal Sync</TabsTrigger>
            <TabsTrigger value="settings" data-testid="events-tab-settings"><SettingsIcon className="w-4 h-4 mr-1" /> Settings</TabsTrigger>
            <TabsTrigger value="embed" data-testid="events-tab-embed"><Code className="w-4 h-4 mr-1" /> Embed</TabsTrigger>
          </TabsList>

          {/* CALENDAR */}
          <TabsContent value="calendar">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              {/* Admin filter bar (Task 3) */}
              <div className="flex flex-wrap gap-2 mb-3 items-center">
                <div className="relative flex-1 min-w-[140px]">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                  <input value={adminFilterSearch} onChange={e => setAdminFilterSearch(e.target.value)}
                    placeholder="Search events…" data-testid="admin-filter-search"
                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300" />
                </div>
                {filterOptions.event_types.length > 0 && (
                  <select value={adminFilterType} onChange={e => setAdminFilterType(e.target.value)}
                    className="py-1.5 pl-3 pr-7 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white"
                    data-testid="admin-filter-type">
                    <option value="">All types</option>
                    {filterOptions.event_types.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                )}
                {filterOptions.departments.length > 0 && (
                  <select value={adminFilterDept} onChange={e => setAdminFilterDept(e.target.value)}
                    className="py-1.5 pl-3 pr-7 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white"
                    data-testid="admin-filter-dept">
                    <option value="">All departments</option>
                    {filterOptions.departments.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                )}
                {(adminFilterType || adminFilterDept || adminFilterSearch || adminFilterSource) && (
                  <button onClick={() => { setAdminFilterType(''); setAdminFilterDept(''); setAdminFilterSearch(''); setAdminFilterSource(''); }}
                    className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1.5 rounded border border-slate-200 hover:bg-slate-50"
                    data-testid="admin-filter-clear">
                    Clear
                  </button>
                )}
                {(adminFilterType || adminFilterDept || adminFilterSearch || adminFilterSource) && (
                  <span className="text-xs text-slate-400">
                    {filteredAdminEvents.length} of {events.length} events
                  </span>
                )}
              </div>
              {/* iCal source filter pills — only shown when sources exist */}
              {icalSources.filter(s => s.events_count > 0 || events.some(e => e.ical_source_id === s.source_id)).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3" data-testid="admin-ical-filter-pills">
                  {icalSources.filter(s => events.some(e => e.ical_source_id === s.source_id)).map(src => (
                    <button key={src.source_id}
                      onClick={() => setAdminFilterSource(adminFilterSource === src.source_id ? '' : src.source_id)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        adminFilterSource === src.source_id
                          ? 'text-white border-transparent'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                      }`}
                      style={adminFilterSource === src.source_id ? { backgroundColor: src.color, borderColor: src.color } : {}}
                      data-testid={`admin-filter-source-${src.source_id}`}>
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: adminFilterSource === src.source_id ? 'rgba(255,255,255,0.7)' : src.color }} />
                      {src.name}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))} data-testid="calendar-prev-btn"><ChevronLeft className="w-4 h-4" /></Button>
                  <h2 className="text-lg font-semibold w-44 text-center">{monthLabel}</h2>
                  <Button variant="outline" size="icon" onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))} data-testid="calendar-next-btn"><ChevronRight className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => setViewDate(new Date())} data-testid="calendar-today-btn">Today</Button>
                </div>
              </div>
              <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden border border-slate-200">
                {WEEKDAYS.map(w => (
                  <div key={w} className="bg-slate-50 text-center text-xs font-semibold text-slate-500 py-2">{w}</div>
                ))}
                {calendarCells.map((cell, i) => {
                  const dayEvents = cell ? (eventsByDay[cell.toDateString()] || []) : [];
                  const isToday = cell && cell.toDateString() === new Date().toDateString();
                  return (
                    <div
                      key={i}
                      className={`bg-white min-h-[104px] p-1.5 ${cell ? 'cursor-pointer hover:bg-slate-50' : 'bg-slate-50/40'}`}
                      onClick={() => cell && openNewEvent(toDateInput(cell))}
                      data-testid={cell ? `calendar-day-${toDateInput(cell)}` : undefined}
                    >
                      {cell && (
                        <>
                          <div className={`text-xs font-medium mb-1 ${isToday ? 'bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center' : 'text-slate-500'}`}>{cell.getDate()}</div>
                          <div className="space-y-1">
                            {dayEvents.slice(0, 3).map(ev => (
                              <div
                                key={ev.event_id}
                                onClick={(e) => { e.stopPropagation(); openEditEvent(ev); }}
                                className="text-[11px] leading-tight px-1.5 py-0.5 rounded truncate text-white flex items-center gap-1"
                                style={{ backgroundColor: ev.color || '#6366f1', opacity: ev.status === 'published' ? 1 : 0.55 }}
                                title={ev.title}
                                data-testid={`calendar-event-${ev.event_id}`}
                              >
                                {ev.status !== 'published' && <EyeOff className="w-2.5 h-2.5 shrink-0" />}
                                <span className="truncate">{ev.title}</span>
                              </div>
                            ))}
                            {dayEvents.length > 3 && (
                              <div className="text-[10px] text-slate-400 pl-1">+{dayEvents.length - 3} more</div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {/* LIST */}
          <TabsContent value="list">
            <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
              {loading ? (
                <div className="p-8 text-center text-slate-400">Loading…</div>
              ) : events.length === 0 ? (
                <div className="p-12 text-center text-slate-400">
                  <CalendarIcon className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  No events yet. Click <strong>New Event</strong> to create one.
                </div>
              ) : events.map(ev => (
                <div key={ev.event_id} className="flex items-center gap-4 p-4 hover:bg-slate-50" data-testid={`event-row-${ev.event_id}`}>
                  <div className="w-1.5 h-12 rounded-full" style={{ backgroundColor: ev.color || '#6366f1' }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-900 truncate">{ev.title}</p>
                      <Badge variant={ev.status === 'published' ? 'default' : 'secondary'} className="text-[10px]">{ev.status}</Badge>
                      {ev.form_id && <Badge variant="outline" className="text-[10px]"><ClipboardList className="w-3 h-3 mr-1" />Form</Badge>}
                      {ev.rsvp_enabled && <Badge variant="outline" className="text-[10px]"><Users className="w-3 h-3 mr-1" />RSVP</Badge>}
                      {ev.ticket_enabled && <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-200"><CreditCard className="w-3 h-3 mr-1" />Ticketed</Badge>}
                      {ev.reminders && ev.reminders.length > 0 && <Badge variant="outline" className="text-[10px]"><Clock className="w-3 h-3 mr-1" />{ev.reminders.length} reminder{ev.reminders.length > 1 ? 's' : ''}</Badge>}
                      {ev.push_sent_at && <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-200"><Bell className="w-3 h-3 mr-1" />Notified</Badge>}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 mt-1">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{ev.start ? new Date(ev.start).toLocaleString() : '—'}</span>
                      {ev.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{ev.location}</span>}
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" />{ev.audience?.whole_school ? 'Everyone' : `${ev.audience?.group_ids?.length || 0} group(s)`}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {taptileConnected && ev.status === 'published' && (
                      <Button variant="ghost" size="icon" onClick={() => sendPushNow(ev)} title="Send push to Taptile app" data-testid={`event-push-${ev.event_id}`}><Bell className="w-4 h-4 text-amber-600" /></Button>
                    )}
                    {ev.rsvp_enabled && (
                      <Button variant="ghost" size="icon" onClick={() => openRsvps(ev)} title="View RSVPs" data-testid={`event-rsvps-${ev.event_id}`}><Users className="w-4 h-4 text-indigo-600" /></Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => togglePublish(ev)} title={ev.status === 'published' ? 'Unpublish' : 'Publish'} data-testid={`event-publish-${ev.event_id}`}>
                      {ev.status === 'published' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4 text-emerald-600" />}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEditEvent(ev)} data-testid={`event-edit-${ev.event_id}`}><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => deleteEvent(ev)} data-testid={`event-delete-${ev.event_id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* GROUPS */}
          <TabsContent value="groups">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-500">Audience groups let you target events to specific classes, years or families.</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="groups-import-btn"><Upload className="w-4 h-4 mr-2" />Import CSV</Button>
                <Button size="sm" onClick={openNewGroup} data-testid="groups-new-btn"><Plus className="w-4 h-4 mr-2" />New Group</Button>
              </div>
            </div>

            {/* Taptile group mapping banner */}
            {taptileConnected ? (
              <div className="mb-4 flex items-start gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 text-sm text-indigo-800" data-testid="taptile-groups-banner">
                <Bell className="w-4 h-4 mt-0.5 shrink-0 text-indigo-500" />
                <div>
                  <span className="font-medium">Taptile push targeting is active.</span> Open any group and paste its Taptile Group ID so event push notifications only reach the right people.
                  {' '}<span className="text-indigo-500 text-xs">Find IDs in your Taptile admin → Contacts → Groups → copy the UUID.</span>
                </div>
              </div>
            ) : (
              <div className="mb-4 flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-600" data-testid="taptile-groups-not-connected">
                <Bell className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
                <div>
                  <span className="font-medium">Connect Taptile to enable push targeting.</span> Once connected, you can map each group to a Taptile Group ID so event pushes reach the right audience.
                  {' '}<a href="/taptile" className="text-indigo-600 underline font-medium">Connect Taptile →</a>
                </div>
              </div>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {groups.length === 0 ? (
                <div className="col-span-full p-12 text-center text-slate-400 bg-white rounded-xl border border-slate-200">
                  <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />No groups yet.
                </div>
              ) : groups.map(g => (
                <div key={g.group_id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`group-card-${g.group_id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-slate-900">{g.name}</p>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <Badge variant="secondary" className="text-[10px] capitalize">{g.type}</Badge>
                        {g.taptile_group_id ? (
                          <Badge variant="outline" className="text-[10px] text-indigo-700 border-indigo-300 bg-indigo-50 gap-1" data-testid={`group-taptile-badge-${g.group_id}`}>
                            <Bell className="w-2.5 h-2.5" /> Taptile linked
                          </Badge>
                        ) : taptileConnected ? (
                          <span className="text-[10px] text-amber-500 flex items-center gap-1" data-testid={`group-taptile-unlinked-${g.group_id}`}>
                            <Bell className="w-2.5 h-2.5" /> No Taptile link
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditGroup(g)}><Edit className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteGroup(g)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 mt-3">{(g.member_emails || []).length} member(s)</p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* TICKETS */}
          <TabsContent value="tickets" data-testid="events-tickets-tab">
            {events.filter(e => e.ticket_enabled).length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400">
                <CreditCard className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="font-medium">No events have ticketing enabled.</p>
                <p className="text-xs mt-1">Open an event editor and toggle "Sell tickets via Stripe".</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-sm font-medium text-slate-600 shrink-0">View tickets for</span>
                  <Select
                    value={ticketEventId}
                    onValueChange={(v) => { setTicketEventId(v); loadTickets(v); }}
                  >
                    <SelectTrigger className="max-w-xs" data-testid="tickets-event-select">
                      <SelectValue placeholder="Select event…" />
                    </SelectTrigger>
                    <SelectContent>
                      {events.filter(e => e.ticket_enabled).map(e => (
                        <SelectItem key={e.event_id} value={e.event_id}>{e.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {ticketEventId && (
                    <button onClick={() => loadTickets(ticketEventId)} className="text-slate-400 hover:text-slate-600" title="Refresh" data-testid="tickets-refresh-btn">
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {ticketsLoading ? (
                  <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">Loading tickets…</div>
                ) : ticketsData ? (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="bg-white rounded-xl border border-slate-200 p-4 text-center" data-testid="tickets-sold-count">
                        <p className="text-2xl font-bold text-slate-900">{ticketsData.total_sold}</p>
                        <p className="text-xs text-slate-500 mt-1">Tickets sold</p>
                      </div>
                      <div className="bg-white rounded-xl border border-emerald-200 p-4 text-center bg-emerald-50/30" data-testid="tickets-revenue">
                        <p className="text-2xl font-bold text-emerald-600">{(ticketsData.currency || 'usd').toUpperCase()} {(ticketsData.total_revenue || 0).toFixed(2)}</p>
                        <p className="text-xs text-slate-500 mt-1">Revenue</p>
                      </div>
                      <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                        <p className="text-2xl font-bold text-indigo-600">{ticketsData.total_checked_in || 0}</p>
                        <p className="text-xs text-slate-500 mt-1">Checked in</p>
                      </div>
                      <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                        <p className="text-2xl font-bold text-slate-400">{ticketsData.tickets?.filter(t => t.payment_status === 'refunded').length || 0}</p>
                        <p className="text-xs text-slate-500 mt-1">Refunded</p>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="text-left p-3 text-xs font-semibold text-slate-500">Buyer</th>
                            <th className="text-left p-3 text-xs font-semibold text-slate-500">Qty</th>
                            <th className="text-left p-3 text-xs font-semibold text-slate-500">Amount</th>
                            <th className="text-left p-3 text-xs font-semibold text-slate-500">Status</th>
                            <th className="text-left p-3 text-xs font-semibold text-slate-500">Check-In</th>
                            <th className="text-left p-3 text-xs font-semibold text-slate-500">Date</th>
                            <th className="p-3 w-20"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {ticketsData.tickets?.length === 0 ? (
                            <tr><td colSpan={6} className="p-8 text-center text-slate-400">No tickets purchased yet</td></tr>
                          ) : ticketsData.tickets?.map(t => (
                            <tr key={t.ticket_id} data-testid={`ticket-row-${t.ticket_id}`}>
                              <td className="p-3">
                                <p className="font-medium text-slate-800">{t.buyer_name}</p>
                                <p className="text-xs text-slate-400">{t.buyer_email}</p>
                              </td>
                              <td className="p-3 text-slate-600">{t.quantity}</td>
                              <td className="p-3 text-slate-600">{(t.currency || 'usd').toUpperCase()} {(t.amount_paid || 0).toFixed(2)}</td>
                              <td className="p-3">
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] ${t.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : t.payment_status === 'refunded' ? 'bg-slate-50 text-slate-500' : 'bg-amber-50 text-amber-600 border-amber-200'}`}
                                  data-testid={`ticket-status-${t.ticket_id}`}
                                >
                                  {t.payment_status}
                                </Badge>
                              </td>
                              <td className="p-3">
                                {t.payment_status === 'paid' ? (
                                  <Button
                                    variant={t.checked_in ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => toggleCheckin(t)}
                                    disabled={checkingIn === t.ticket_id}
                                    className={`text-xs ${t.checked_in ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'text-slate-600'}`}
                                    data-testid={`ticket-checkin-${t.ticket_id}`}
                                  >
                                    {checkingIn === t.ticket_id ? '…' : t.checked_in ? 'Checked In' : 'Check In'}
                                  </Button>
                                ) : <span className="text-xs text-slate-300">—</span>}
                              </td>
                              <td className="p-3 text-xs text-slate-400">{new Date(t.created_at).toLocaleDateString()}</td>
                              <td className="p-3">
                                {t.payment_status === 'paid' && (
                                  <Button
                                    variant="ghost" size="sm"
                                    onClick={() => refundTicket(t)}
                                    disabled={refunding === t.ticket_id}
                                    className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                                    data-testid={`ticket-refund-${t.ticket_id}`}
                                  >
                                    {refunding === t.ticket_id ? 'Refunding…' : 'Refund'}
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : null}
              </div>
            )}
          </TabsContent>

          {/* ABSENTEES */}
          <TabsContent value="absentees">
            <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
              {absentees.length === 0 ? (
                <div className="p-12 text-center text-slate-400"><UserX className="w-10 h-10 mx-auto mb-3 opacity-40" />No absence reports yet.</div>
              ) : absentees.map(a => (
                <div key={a.absentee_id} className="p-4" data-testid={`absentee-row-${a.absentee_id}`}>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-slate-900">{a.student_name}</p>
                    <span className="text-xs text-slate-400">{new Date(a.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-sm text-slate-500 mt-1">
                    <span>{a.date_from}{a.date_to ? ` → ${a.date_to}` : ''}</span> · <span>{a.reason}</span>
                    {a.parent_name && <> · Reported by {a.parent_name}{a.parent_email ? ` (${a.parent_email})` : ''}</>}
                  </div>
                  {a.notes && <p className="text-sm text-slate-400 mt-1">{a.notes}</p>}
                </div>
              ))}
            </div>
          </TabsContent>

          {/* SETTINGS */}
          <TabsContent value="settings">
            <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl space-y-5">
              <div className="flex items-center justify-between border border-slate-200 rounded-lg p-4">
                <div>
                  <Label className="!mb-0">Show "Report an absence" link publicly</Label>
                  <p className="text-xs text-slate-400 mt-1">Turn off if your organisation doesn't need absence reporting.</p>
                </div>
                <Switch
                  checked={settings.absentee_enabled}
                  onCheckedChange={(v) => setSettings(s => ({ ...s, absentee_enabled: v }))}
                  data-testid="settings-absentee-enabled"
                />
              </div>
              <div>
                <Label>Office notification email</Label>
                <Input
                  value={settings.office_email}
                  onChange={(e) => setSettings(s => ({ ...s, office_email: e.target.value }))}
                  placeholder="office@yourschool.com"
                  data-testid="settings-office-email"
                />
                <p className="text-xs text-slate-400 mt-1">Absence notifications from your public site are sent here.</p>
              </div>
              <div>
                <Label>Absentee auto-responder message</Label>
                <Textarea
                  rows={3}
                  value={settings.absentee_auto_responder}
                  onChange={(e) => setSettings(s => ({ ...s, absentee_auto_responder: e.target.value }))}
                  data-testid="settings-absentee-responder"
                />
              </div>
              {publicCalUrl && (
                <div>
                  <Label>Public iCal feed URL</Label>
                  <div className="flex gap-2">
                    <Input readOnly value={publicCalUrl} data-testid="settings-ical-url" />
                    <Button variant="outline" onClick={() => { navigator.clipboard.writeText(publicCalUrl); toast.success('Copied'); }}>Copy</Button>
                  </div>
                </div>
              )}

              {/* API Inbound Events */}
              <div className="border rounded-xl p-4 space-y-1 bg-slate-50/50">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Auto-publish inbound API events</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      When <strong>on</strong>, events pushed by third-party platforms (e.g. Gametime) via the API go straight to published.
                      When <strong>off</strong>, they arrive as drafts for you to review first.
                    </p>
                  </div>
                  <Switch
                    checked={settings.api_auto_publish}
                    onCheckedChange={v => setSettings(s => ({ ...s, api_auto_publish: v }))}
                    data-testid="api-auto-publish-toggle"
                  />
                </div>
              </div>

              {/* Manage Labels */}
              <div className="border-t pt-4 space-y-4" data-testid="manage-labels-section">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-800">Manage Filter Labels</h3>
                  <button onClick={refreshFilterOptions} className="text-xs text-indigo-600 hover:underline flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Refresh</button>
                </div>
                <p className="text-xs text-slate-500">Remove a label to clear it from all events that use it. This cannot be undone.</p>

                {/* Event Types */}
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1"><Tag className="w-3 h-3" /> Event Types</p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {filterOptions.event_types.length === 0 ? (
                      <span className="text-xs text-slate-400 italic">No event types yet — add one below</span>
                    ) : filterOptions.event_types.map(t => {
                      const count = events.filter(e => e.event_type === t).length;
                      return (
                        <span key={t} className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-700 text-xs px-2.5 py-1 rounded-full" data-testid={`label-type-${t}`}>
                          {t} <span className="text-slate-400">({count})</span>
                          <button type="button" onClick={() => deleteLabel('event_type', t, count)} className="text-slate-400 hover:text-red-500 ml-0.5" title="Remove from all events">×</button>
                        </span>
                      );
                    })}
                  </div>
                  <div className="flex gap-2">
                    <input
                      value={newTypeInput}
                      onChange={e => setNewTypeInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLabel('event_type', newTypeInput, setNewTypeInput); } }}
                      placeholder="e.g. Workshop, Conference…"
                      className="flex-1 px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300"
                      data-testid="add-label-type-input"
                    />
                    <button type="button" onClick={() => addLabel('event_type', newTypeInput, setNewTypeInput)}
                      className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs hover:bg-indigo-700" data-testid="add-label-type-btn">+ Add</button>
                  </div>
                </div>

                {/* Departments */}
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1"><Filter className="w-3 h-3" /> Departments / Teams</p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {filterOptions.departments.length === 0 ? (
                      <span className="text-xs text-slate-400 italic">No departments yet — add one below</span>
                    ) : filterOptions.departments.map(d => {
                      const count = events.filter(e => (e.departments || []).includes(d)).length;
                      return (
                        <span key={d} className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-700 text-xs px-2.5 py-1 rounded-full" data-testid={`label-dept-${d}`}>
                          {d} <span className="text-indigo-300">({count})</span>
                          <button type="button" onClick={() => deleteLabel('departments', d, count)} className="text-indigo-300 hover:text-red-500 ml-0.5" title="Remove from all events">×</button>
                        </span>
                      );
                    })}
                  </div>
                  <div className="flex gap-2">
                    <input
                      value={newDeptInput}
                      onChange={e => setNewDeptInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLabel('departments', newDeptInput, setNewDeptInput); } }}
                      placeholder="e.g. Sales, Marketing, Finance…"
                      className="flex-1 px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300"
                      data-testid="add-label-dept-input"
                    />
                    <button type="button" onClick={() => addLabel('departments', newDeptInput, setNewDeptInput)}
                      className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs hover:bg-indigo-700" data-testid="add-label-dept-btn">+ Add</button>
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1"><Tag className="w-3 h-3" /> Tags</p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {filterOptions.tags.length === 0 ? (
                      <span className="text-xs text-slate-400 italic">No tags yet — add one below</span>
                    ) : filterOptions.tags.map(t => {
                      const count = events.filter(e => (e.tags || []).includes(t)).length;
                      return (
                        <span key={t} className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 text-xs px-2.5 py-1 rounded-full" data-testid={`label-tag-${t}`}>
                          {t} <span className="text-emerald-300">({count})</span>
                          <button type="button" onClick={() => deleteLabel('tags', t, count)} className="text-emerald-300 hover:text-red-500 ml-0.5" title="Remove from all events">×</button>
                        </span>
                      );
                    })}
                  </div>
                  <div className="flex gap-2">
                    <input
                      value={newTagInput}
                      onChange={e => setNewTagInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLabel('tags', newTagInput, setNewTagInput); } }}
                      placeholder="e.g. annual, external-speaker…"
                      className="flex-1 px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300"
                      data-testid="add-label-tag-input"
                    />
                    <button type="button" onClick={() => addLabel('tags', newTagInput, setNewTagInput)}
                      className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs hover:bg-indigo-700" data-testid="add-label-tag-btn">+ Add</button>
                  </div>
                </div>
              </div>

              <Button onClick={saveSettings} data-testid="settings-save-btn">Save Settings</Button>
            </div>
          </TabsContent>

          {/* ========== iCal Sync Tab ========== */}
          <TabsContent value="ical">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Inbound iCal Sync</h2>
                  <p className="text-sm text-slate-500 mt-0.5">Pull events from any external calendar (Google Calendar, Outlook, Apple Calendar, etc.) into Nexus automatically.</p>
                </div>
                <Button onClick={() => { setIcalForm({ name: '', url: '', color: '#6366f1', sync_interval_hours: 6, _urlVisible: false }); setEditingIcal(null); setIcalFormOpen(true); }} data-testid="ical-add-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Source
                </Button>
              </div>

              {icalSources.length === 0 ? (
                <div className="bg-slate-50 rounded-xl border-2 border-dashed border-slate-200 p-10 text-center text-slate-400">
                  <Link2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p className="font-medium">No iCal sources yet</p>
                  <p className="text-sm mt-1">Add a Google Calendar, Outlook, or any public .ics URL to automatically import events.</p>
                  <Button className="mt-4" onClick={() => { setIcalForm({ name: '', url: '', color: '#6366f1', sync_interval_hours: 6 }); setEditingIcal(null); setIcalFormOpen(true); }}>
                    Add your first source
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {icalSources.map(src => (
                    <div key={src.source_id} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-4" data-testid={`ical-source-${src.source_id}`}>
                      <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: src.color }} />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900">{src.name}</p>
                        <IcalUrlMasked url={src.url} />
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            {src.last_sync_status === 'ok' ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : src.last_sync_status === 'error' ? <AlertCircle className="w-3.5 h-3.5 text-red-400" /> : <Clock className="w-3.5 h-3.5 text-slate-400" />}
                            {src.last_synced ? `Synced ${new Date(src.last_synced).toLocaleString()}` : 'Never synced'}
                          </span>
                          <span>{src.events_count || 0} event{src.events_count !== 1 ? 's' : ''} imported</span>
                          {src.last_sync_status === 'error' && (
                            <span className="text-red-400 truncate max-w-xs">{src.last_sync_error}</span>
                          )}
                          {src.last_sync_status === 'error' && src.url?.includes('calendar.google.com') && (
                            <span className="text-amber-600 text-xs">
                              Tip: ensure the calendar is set to public, or use the Secret address in iCal format from Google Calendar → Settings → Integrate calendar.
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button variant="outline" size="sm" onClick={() => syncNow(src)} disabled={icalSyncing === src.source_id} data-testid={`ical-sync-btn-${src.source_id}`}>
                          {icalSyncing === src.source_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sync now
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => { setEditingIcal(src); setIcalForm({ name: src.name, url: src.url, color: src.color, sync_interval_hours: src.sync_interval_hours || 6, _urlVisible: false }); setIcalFormOpen(true); }}><Edit className="w-4 h-4" /></Button>
                        <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700" onClick={() => deleteIcalSource(src)} data-testid={`ical-delete-btn-${src.source_id}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-700">
                <p className="font-semibold mb-1">How to get your iCal URL</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>
                    <strong>Google Calendar:</strong> Settings → Your calendar → Scroll to "Integrate calendar" →
                    copy either the <strong>"Public address in iCal format"</strong> (calendar must be set to public)
                    or the <strong>"Secret address in iCal format"</strong> (recommended — keeps events private and off Google search results, no public setting needed)
                  </li>
                  <li><strong>Outlook / Microsoft 365:</strong> Calendar → Shared → Publish → copy the ICS link</li>
                  <li><strong>Apple Calendar:</strong> Calendar → Share Calendar → enable Public Calendar → copy URL</li>
                  <li>Any <code>.ics</code> URL works — school MIS systems, sports fixtures, booking systems, etc.</li>
                </ul>
              </div>
            </div>
          </TabsContent>

          {/* ── Embed ───────────────────────────────────────── */}
          <TabsContent value="embed" data-testid="events-tab-embed-content">
            <div className="space-y-6 max-w-2xl">
              <div>
                <h2 className="text-xl font-bold text-slate-800">Embed Calendar on Your Website</h2>
                <p className="text-sm text-slate-500 mt-0.5">Copy the snippet below and paste it into any webpage to display your events calendar inside an iframe.</p>
              </div>

              {!siteSlug ? (
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
                  Loading your calendar URL… please wait a moment.
                </div>
              ) : (
                <>
                  {/* Options */}
                  <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-5">
                    <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Customise snippet</h3>

                    {/* Default view */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-800">Default view</p>
                        <p className="text-xs text-slate-500">Which view opens when the page loads</p>
                      </div>
                      <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
                        <button
                          onClick={() => setEmbedView('calendar')}
                          className={`px-3 py-1.5 text-sm rounded-md transition-colors ${embedView === 'calendar' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                          data-testid="embed-view-calendar"
                        >Calendar</button>
                        <button
                          onClick={() => setEmbedView('list')}
                          className={`px-3 py-1.5 text-sm rounded-md transition-colors ${embedView === 'list' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                          data-testid="embed-view-list"
                        >List</button>
                      </div>
                    </div>

                    {/* Height */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-800">Height</p>
                        <p className="text-xs text-slate-500">Fixed pixel height of the iframe</p>
                      </div>
                      <Select value={embedHeight} onValueChange={setEmbedHeight}>
                        <SelectTrigger className="w-36" data-testid="embed-height-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="400">400 px</SelectItem>
                          <SelectItem value="600">600 px</SelectItem>
                          <SelectItem value="700">700 px</SelectItem>
                          <SelectItem value="800">800 px</SelectItem>
                          <SelectItem value="900">900 px</SelectItem>
                          <SelectItem value="1000">1000 px</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <hr className="border-slate-100" />

                    {/* Show filter pills */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-800">Show filter bar</p>
                        <p className="text-xs text-slate-500">Search box and calendar/event-type filter pills</p>
                      </div>
                      <Switch checked={embedShowFilters} onCheckedChange={setEmbedShowFilters} data-testid="embed-toggle-filters" />
                    </div>

                    {/* Show iCal button */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-800">Show "Subscribe to iCal" button</p>
                        <p className="text-xs text-slate-500">Lets visitors download the .ics feed</p>
                      </div>
                      <Switch checked={embedShowIcal} onCheckedChange={setEmbedShowIcal} data-testid="embed-toggle-ical" />
                    </div>

                    {/* Show absence button */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-800">Show "Report an absence" button</p>
                        <p className="text-xs text-slate-500">Lets visitors submit an absence from the embedded calendar</p>
                      </div>
                      <Switch checked={embedShowAbsence} onCheckedChange={setEmbedShowAbsence} data-testid="embed-toggle-absence" />
                    </div>
                  </div>

                  {/* Generated snippet */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-slate-700">Your embed code</p>
                      <Button
                        size="sm"
                        variant={embedCopied ? 'default' : 'outline'}
                        onClick={copyEmbed}
                        className={embedCopied ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
                        data-testid="embed-copy-btn"
                      >
                        {embedCopied ? <><Check className="w-4 h-4 mr-1" /> Copied!</> : <><Copy className="w-4 h-4 mr-1" /> Copy snippet</>}
                      </Button>
                    </div>
                    <pre
                      className="bg-slate-900 text-emerald-400 text-xs rounded-xl p-4 overflow-x-auto whitespace-pre-wrap break-all leading-relaxed font-mono select-all"
                      data-testid="embed-snippet-code"
                    >{embedSnippet}</pre>
                  </div>

                  {/* Live preview hint */}
                  <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4 text-sm text-indigo-700 space-y-1">
                    <p className="font-semibold">How to use</p>
                    <ol className="list-decimal list-inside space-y-1 text-xs text-indigo-600">
                      <li>Copy the snippet above.</li>
                      <li>Paste it into any webpage's HTML where you want the calendar to appear.</li>
                      <li>The calendar fills the full width of its container at the height you chose.</li>
                      <li>No scripts or plugins required — just plain HTML.</li>
                    </ol>
                  </div>

                  {/* Preview link */}
                  <div className="text-xs text-slate-500">
                    Preview URL: <a href={embedSrc} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline break-all" data-testid="embed-preview-link">{embedSrc}</a>
                  </div>
                </>
              )}
            </div>
          </TabsContent>

        </Tabs>
      </div>

      {/* iCal Source Dialog */}
      <Dialog open={icalFormOpen} onOpenChange={setIcalFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingIcal ? 'Edit iCal Source' : 'Add iCal Source'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Name *</Label>
              <Input value={icalForm.name} onChange={e => setIcalForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Google Calendar — Sales Team" data-testid="ical-form-name" />
            </div>
            <div>
              <Label>iCal URL *</Label>
              <div className="relative">
                <Input
                  type={icalForm._urlVisible ? 'text' : 'password'}
                  value={icalForm.url}
                  onChange={e => setIcalForm(f => ({ ...f, url: e.target.value }))}
                  placeholder="https://calendar.google.com/calendar/ical/…/private/secret.ics"
                  data-testid="ical-form-url"
                  className="pr-9 font-mono text-xs"
                />
                <button type="button"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setIcalForm(f => ({ ...f, _urlVisible: !f._urlVisible }))}
                  title={icalForm._urlVisible ? 'Hide URL' : 'Show URL'}>
                  {icalForm._urlVisible ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-1">Paste any <code>.ics</code> URL. The <strong>Secret address</strong> from Google Calendar is recommended to keep events off search results, but the public address also works.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Colour</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5 max-w-xs">
                  {COLORS.map(c => (
                    <button key={c} type="button" onClick={() => setIcalForm(f => ({ ...f, color: c }))}
                      title={c}
                      className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${icalForm.color === c ? 'border-slate-800 scale-110 ring-2 ring-offset-1 ring-slate-400' : 'border-transparent'}`}
                      style={{ backgroundColor: c }} />
                  ))}
                  {/* Custom colour picker — any hex value */}
                  <label className="relative w-6 h-6 rounded-full cursor-pointer border-2 border-dashed border-slate-300 hover:border-slate-500 transition-colors flex items-center justify-center overflow-hidden" title="Custom colour">
                    <span className="text-slate-400 text-xs font-bold leading-none select-none">+</span>
                    <input type="color" value={icalForm.color} onChange={e => setIcalForm(f => ({ ...f, color: e.target.value }))}
                      className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
                  </label>
                </div>
                <p className="text-xs text-slate-400 mt-1 font-mono">{icalForm.color}</p>
              </div>
              <div>
                <Label>Auto-sync every</Label>
                <select value={icalForm.sync_interval_hours} onChange={e => setIcalForm(f => ({ ...f, sync_interval_hours: parseInt(e.target.value) }))}
                  className="mt-1 w-full py-2 pl-3 pr-8 text-sm border border-slate-200 rounded-lg focus:outline-none bg-white" data-testid="ical-form-interval">
                  <option value={1}>1 hour</option>
                  <option value={3}>3 hours</option>
                  <option value={6}>6 hours</option>
                  <option value={12}>12 hours</option>
                  <option value={24}>24 hours</option>
                </select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIcalFormOpen(false); setEditingIcal(null); }}>Cancel</Button>
            <Button onClick={saveIcalSource} data-testid="ical-form-save">{editingIcal ? 'Save Changes' : 'Add & Sync'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EVENT DIALOG */}
      <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingEvent ? 'Edit Event' : 'New Event'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={eventForm.title} onChange={(e) => setEventForm(f => ({ ...f, title: e.target.value }))} data-testid="event-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start *</Label>
                <Input type="datetime-local" value={eventForm.start} onChange={(e) => setEventForm(f => ({ ...f, start: e.target.value }))} data-testid="event-start-input" />
              </div>
              <div>
                <Label>End</Label>
                <Input type="datetime-local" value={eventForm.end} onChange={(e) => setEventForm(f => ({ ...f, end: e.target.value }))} data-testid="event-end-input" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={eventForm.all_day} onCheckedChange={(v) => setEventForm(f => ({ ...f, all_day: v }))} data-testid="event-allday-switch" />
              <Label className="!mb-0">All-day event</Label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Location</Label>
                <Input value={eventForm.location} onChange={(e) => setEventForm(f => ({ ...f, location: e.target.value }))} data-testid="event-location-input" />
              </div>
              <div>
                <Label>Category</Label>
                <Input value={eventForm.category} onChange={(e) => setEventForm(f => ({ ...f, category: e.target.value }))} placeholder="Sports, Academic…" />
              </div>
            </div>
            <div>
              <Label>Colour</Label>
              <div className="flex flex-wrap gap-1.5 mt-1 max-w-xs">
                {COLORS.map(c => (
                  <button key={c} type="button" onClick={() => setEventForm(f => ({ ...f, color: c }))}
                    title={c}
                    className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${eventForm.color === c ? 'border-slate-900 scale-110 ring-2 ring-offset-1 ring-slate-400' : 'border-transparent'}`}
                    style={{ backgroundColor: c }} data-testid={`event-color-${c}`} />
                ))}
                <label className="relative w-6 h-6 rounded-full cursor-pointer border-2 border-dashed border-slate-300 hover:border-slate-500 transition-colors flex items-center justify-center overflow-hidden" title="Custom colour">
                  <span className="text-slate-400 text-xs font-bold leading-none select-none">+</span>
                  <input type="color" value={eventForm.color} onChange={e => setEventForm(f => ({ ...f, color: e.target.value }))}
                    className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
                </label>
              </div>
              <p className="text-xs text-slate-400 mt-1 font-mono">{eventForm.color}</p>
            </div>

            {/* Classification / Filtering */}
            <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-3 space-y-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Classification &amp; Filtering</p>
              <div>
                <Label className="text-xs">Event Type</Label>
                <Input
                  value={eventForm.event_type}
                  onChange={(e) => setEventForm(f => ({ ...f, event_type: e.target.value }))}
                  placeholder="e.g. Workshop, Conference, Meeting…"
                  list="event-type-suggestions"
                  data-testid="event-type-input"
                />
                <datalist id="event-type-suggestions">
                  {filterOptions.event_types.map(t => <option key={t} value={t} />)}
                </datalist>
              </div>
              <div>
                <Label className="text-xs">Departments / Teams</Label>
                <div className="flex gap-2">
                  <Input
                    value={deptInput}
                    onChange={(e) => setDeptInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDept(deptInput); } }}
                    placeholder="Type and press Enter…"
                    list="dept-suggestions"
                    className="flex-1"
                    data-testid="event-dept-input"
                  />
                  <datalist id="dept-suggestions">
                    {filterOptions.departments.map(d => <option key={d} value={d} />)}
                  </datalist>
                  <button type="button" onClick={() => addDept(deptInput)} className="px-3 py-1.5 rounded-lg bg-slate-200 text-slate-700 text-xs hover:bg-slate-300">Add</button>
                </div>
                {eventForm.departments.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {eventForm.departments.map(d => (
                      <span key={d} className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 text-xs px-2.5 py-1 rounded-full" data-testid={`event-dept-tag-${d}`}>
                        {d}
                        <button type="button" onClick={() => removeDept(d)} className="hover:text-indigo-900 ml-0.5">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs">Tags</Label>
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(tagInput); } }}
                    placeholder="Type and press Enter…"
                    list="tag-suggestions"
                    className="flex-1"
                    data-testid="event-tag-input"
                  />
                  <datalist id="tag-suggestions">
                    {filterOptions.tags.map(t => <option key={t} value={t} />)}
                  </datalist>
                  <button type="button" onClick={() => addTag(tagInput)} className="px-3 py-1.5 rounded-lg bg-slate-200 text-slate-700 text-xs hover:bg-slate-300">Add</button>
                </div>
                {eventForm.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {eventForm.tags.map(t => (
                      <span key={t} className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-xs px-2.5 py-1 rounded-full" data-testid={`event-tag-chip-${t}`}>
                        {t}
                        <button type="button" onClick={() => removeTag(t)} className="hover:text-emerald-900 ml-0.5">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <div className="border border-slate-200 rounded-lg overflow-hidden mt-1">
                <TipTapEditor content={eventForm.description_html} onChange={(html) => setEventForm(f => ({ ...f, description_html: html }))} placeholder="Event details…" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Cost (optional)</Label>
                <Input type="number" value={eventForm.cost} onChange={(e) => setEventForm(f => ({ ...f, cost: e.target.value }))} placeholder="0.00" data-testid="event-cost-input" />
              </div>
              <div>
                <Label>Capacity (optional)</Label>
                <Input type="number" value={eventForm.capacity} onChange={(e) => setEventForm(f => ({ ...f, capacity: e.target.value }))} data-testid="event-capacity-input" />
              </div>
            </div>
            <div>
              <Label>Linked form (optional)</Label>
              <Select value={eventForm.form_id || 'none'} onValueChange={(v) => setEventForm(f => ({ ...f, form_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="event-form-select"><SelectValue placeholder="No form" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No form</SelectItem>
                  {forms.map(fm => <SelectItem key={fm.form_id} value={fm.form_id}>{fm.internal_name || fm.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">Link an existing form (e.g. excursion consent, RSVP).</p>
            </div>
            <div>
              <Label>External link URL (optional)</Label>
              <Input
                type="url"
                placeholder="https://your-existing-form.com/register"
                value={eventForm.external_link_url}
                onChange={(e) => setEventForm(f => ({ ...f, external_link_url: e.target.value }))}
                data-testid="event-external-link-input"
              />
              <p className="text-xs text-slate-400 mt-1">Link to an existing page or form on any external website (shown as a button on the event listing).</p>
            </div>
            <div className="rounded-lg border border-slate-200 p-3 space-y-3">
              <div className="flex items-center gap-2">
                <Switch checked={eventForm.rsvp_enabled} onCheckedChange={(v) => setEventForm(f => ({ ...f, rsvp_enabled: v }))} data-testid="event-rsvp-switch" />
                <Label className="!mb-0">Enable RSVP / registration</Label>
              </div>
              {eventForm.rsvp_enabled && (
                <p className="text-xs text-slate-400">
                  Visitors can register with a party size. Set a <strong>Capacity</strong> above to show "spots left" and close sign-ups when full. Leave capacity empty for unlimited.
                </p>
              )}
              {eventForm.rsvp_enabled && eventForm.capacity !== '' && (
                <div className="border-t border-slate-100 pt-3 space-y-3">
                  <div className="flex items-center gap-2">
                    <Switch checked={eventForm.waitlist_enabled} onCheckedChange={(v) => setEventForm(f => ({ ...f, waitlist_enabled: v }))} data-testid="event-waitlist-switch" />
                    <Label className="!mb-0">Enable waitlist when full</Label>
                  </div>
                  {eventForm.waitlist_enabled && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Claim window (hours)</Label>
                        <Input type="number" min="1" value={eventForm.waitlist_claim_hours} onChange={(e) => setEventForm(f => ({ ...f, waitlist_claim_hours: e.target.value }))} data-testid="event-waitlist-hours" />
                      </div>
                      <div>
                        <Label className="text-xs">Max waitlist size</Label>
                        <Input type="number" min="1" value={eventForm.waitlist_max} onChange={(e) => setEventForm(f => ({ ...f, waitlist_max: e.target.value }))} placeholder="Unlimited" data-testid="event-waitlist-max" />
                      </div>
                      <p className="col-span-2 text-xs text-slate-400">When someone cancels, the next person on the waitlist is emailed a claim link that expires after the claim window, then rolls to the next person.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="rounded-lg border border-slate-200 p-3 space-y-3">
              <Label className="!mb-0 flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" /> Email reminders</Label>
              <p className="text-xs text-slate-400 -mt-1">Sent automatically to this event's audience before it starts.</p>
              <div className="flex flex-wrap gap-2">
                {REMINDER_PRESETS.map(p => {
                  const active = eventForm.reminderPresets.includes(p.minutes);
                  return (
                    <button key={p.minutes} type="button" onClick={() => togglePreset(p.minutes)}
                      className={`text-xs px-3 py-1.5 rounded-full border ${active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200'}`}
                      data-testid={`event-reminder-preset-${p.minutes}`}>
                      {p.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-end gap-2">
                <div>
                  <Label className="text-xs">Custom offset</Label>
                  <Input type="number" min="1" className="w-24" value={eventForm.customReminderValue} onChange={(e) => setEventForm(f => ({ ...f, customReminderValue: e.target.value }))} placeholder="e.g. 5" data-testid="event-reminder-custom-value" />
                </div>
                <Select value={eventForm.customReminderUnit} onValueChange={(v) => setEventForm(f => ({ ...f, customReminderUnit: v }))}>
                  <SelectTrigger className="w-28" data-testid="event-reminder-custom-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hours">hours before</SelectItem>
                    <SelectItem value="days">days before</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Extra recipients (one per line)</Label>
                <Textarea rows={2} value={eventForm.reminder_extra_recipients} onChange={(e) => setEventForm(f => ({ ...f, reminder_extra_recipients: e.target.value }))} placeholder="principal@school.com" data-testid="event-reminder-extra" />
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-slate-400" />
                  <Label className="!mb-0">Notify the Taptile app when published</Label>
                </div>
                <Switch
                  checked={eventForm.send_push}
                  onCheckedChange={(v) => setEventForm(f => ({ ...f, send_push: v }))}
                  disabled={!taptileConnected}
                  data-testid="event-push-switch"
                />
              </div>
              <p className="text-xs text-slate-400">
                {taptileConnected
                  ? 'Sends a push notification to your Taptile app the first time this event goes live. Group-targeted events use your mapped Taptile groups (whole-audience → everyone).'
                  : 'Connect your Taptile account in the integrations settings to enable app push notifications.'}
              </p>
            </div>
            {/* TICKETING */}
            <div className="rounded-lg border border-indigo-100 bg-indigo-50/30 p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-indigo-500" />
                  <Label className="!mb-0">Sell tickets via Stripe</Label>
                </div>
                <Switch
                  checked={eventForm.ticket_enabled}
                  onCheckedChange={(v) => setEventForm(f => ({ ...f, ticket_enabled: v }))}
                  data-testid="event-ticket-switch"
                />
              </div>
              {eventForm.ticket_enabled && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Ticket name</Label>
                      <input
                        className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
                        value={eventForm.ticket_name}
                        onChange={(e) => setEventForm(f => ({ ...f, ticket_name: e.target.value }))}
                        placeholder="General Admission"
                        data-testid="event-ticket-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Currency</Label>
                      <Select value={eventForm.ticket_currency} onValueChange={(v) => setEventForm(f => ({ ...f, ticket_currency: v }))}>
                        <SelectTrigger className="mt-1" data-testid="event-ticket-currency"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="usd">USD ($)</SelectItem>
                          <SelectItem value="gbp">GBP (£)</SelectItem>
                          <SelectItem value="eur">EUR (€)</SelectItem>
                          <SelectItem value="aud">AUD (A$)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Price per ticket</Label>
                      <input
                        type="number" min="0.50" step="0.01"
                        className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
                        value={eventForm.ticket_price}
                        onChange={(e) => setEventForm(f => ({ ...f, ticket_price: e.target.value }))}
                        placeholder="0.00"
                        data-testid="event-ticket-price-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Max tickets per order</Label>
                      <input
                        type="number" min="1" max="100"
                        className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
                        value={eventForm.ticket_limit_per_order}
                        onChange={(e) => setEventForm(f => ({ ...f, ticket_limit_per_order: e.target.value }))}
                        data-testid="event-ticket-limit-input"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-indigo-600">
                    Buyers pay via Stripe Checkout. A confirmation email is sent on payment. View purchases and issue refunds from the <strong>Tickets</strong> tab.
                  </p>
                  {!stripeConnectStatus?.charges_enabled && (
                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5" data-testid="stripe-connect-warning">
                      <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-700">
                        <strong>Connect your Stripe account</strong> to receive ticket payouts directly to your bank.
                        Without it, payments are held by the platform.{' '}
                        <a href="/stripe-payments" className="underline font-medium hover:text-amber-900">
                          Set up Stripe Payments →
                        </a>
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
            <div>
              <Label>Audience</Label>
              <div className="flex items-center gap-2 mt-1 mb-2">
                <Switch checked={eventForm.audience.whole_school} onCheckedChange={(v) => setEventForm(f => ({ ...f, audience: { ...f.audience, whole_school: v } }))} data-testid="event-wholeschool-switch" />
                <Label className="!mb-0">Everyone</Label>
              </div>
              {!eventForm.audience.whole_school && (
                <div className="flex flex-wrap gap-2">
                  {groups.length === 0 && <p className="text-xs text-slate-400">No groups yet — create groups under the Groups tab.</p>}
                  {groups.map(g => {
                    const active = eventForm.audience.group_ids.includes(g.group_id);
                    return (
                      <button key={g.group_id} type="button" onClick={() => toggleGroupInAudience(g.group_id)}
                        className={`text-xs px-3 py-1.5 rounded-full border ${active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200'}`}
                        data-testid={`event-audience-group-${g.group_id}`}>
                        {g.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <Label>Status</Label>
              <Select value={eventForm.status} onValueChange={(v) => setEventForm(f => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="event-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft (hidden)</SelectItem>
                  <SelectItem value="published">Published (live)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEventDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveEvent} disabled={saving} data-testid="event-save-btn">{saving ? 'Saving…' : 'Save Event'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GROUP DIALOG */}
      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingGroup ? 'Edit Group' : 'New Group'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input value={groupForm.name} onChange={(e) => setGroupForm(f => ({ ...f, name: e.target.value }))} data-testid="group-name-input" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={groupForm.type} onValueChange={(v) => setGroupForm(f => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="group-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{GROUP_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Member emails (one per line)</Label>
              <Textarea rows={5} value={groupForm.member_emails} onChange={(e) => setGroupForm(f => ({ ...f, member_emails: e.target.value }))} placeholder="parent1@example.com&#10;parent2@example.com" data-testid="group-emails-input" />
            </div>
            {taptileConnected && (
              <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 space-y-2">
                <Label className="text-indigo-800 font-medium flex items-center gap-1.5">
                  <Bell className="w-3.5 h-3.5" /> Taptile Push Targeting (optional)
                </Label>
                <Input
                  value={groupForm.taptile_group_id}
                  onChange={(e) => setGroupForm(f => ({ ...f, taptile_group_id: e.target.value }))}
                  placeholder="e.g. grp_a1b2c3d4-e5f6-..."
                  className="bg-white"
                  data-testid="group-taptile-id-input"
                />
                <p className="text-xs text-indigo-700 leading-relaxed">
                  Paste the Taptile Group ID for this group. Event push notifications targeted at this group will only reach that Taptile audience.
                  <br />
                  <span className="text-indigo-500">Find it in your Taptile admin → Contacts → Groups → click the group → copy the UUID from the URL or detail panel.</span>
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveGroup} data-testid="group-save-btn">Save Group</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* IMPORT DIALOG */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Import Members from CSV</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New group name</Label>
              <Input value={importForm.name} onChange={(e) => setImportForm(f => ({ ...f, name: e.target.value }))} placeholder="Imported Group" data-testid="import-name-input" />
            </div>
            <div>
              <Label>CSV data (paste emails; name,email also works)</Label>
              <Textarea rows={7} value={importForm.csv_text} onChange={(e) => setImportForm(f => ({ ...f, csv_text: e.target.value }))} placeholder="John,john@example.com&#10;jane@example.com" data-testid="import-csv-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
            <Button onClick={doImport} data-testid="import-submit-btn">Import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RSVP VIEWER DIALOG */}
      <Dialog open={rsvpDialogOpen} onOpenChange={setRsvpDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>RSVPs — {rsvpEvent?.title}</DialogTitle></DialogHeader>
          {!rsvpData ? (
            <div className="p-6 text-center text-slate-400">Loading…</div>
          ) : (
            <>
              <div className="flex gap-4 text-sm text-slate-600 mb-3" data-testid="rsvp-summary">
                <span><strong>{rsvpData.confirmed_count}</strong> registered</span>
                <span><strong>{rsvpData.total_seats}</strong> seats</span>
                {rsvpData.capacity != null && <span><strong>{Math.max(rsvpData.capacity - rsvpData.total_seats, 0)}</strong> spots left</span>}
              </div>
              <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
                {rsvpData.rsvps.filter(r => r.status === 'confirmed').length === 0 ? (
                  <div className="p-6 text-center text-slate-400 text-sm">No confirmed RSVPs yet.</div>
                ) : rsvpData.rsvps.filter(r => r.status === 'confirmed').map(r => (
                  <div key={r.rsvp_id} className="flex items-center justify-between p-3" data-testid={`rsvp-item-${r.rsvp_id}`}>
                    <div>
                      <p className="font-medium text-slate-800 text-sm">{r.name}</p>
                      <p className="text-xs text-slate-500">{r.email} · {r.party_size} seat(s)</p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeRsvp(r.rsvp_id)} data-testid={`rsvp-remove-${r.rsvp_id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                ))}
              </div>

              {rsvpData.waitlist_enabled && (
                <div className="mt-5">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1"><Clock className="w-4 h-4" /> Waitlist ({rsvpData.waitlist_active_count} waiting)</h4>
                  <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
                    {rsvpData.waitlist.filter(w => ['waiting', 'offered'].includes(w.status)).length === 0 ? (
                      <div className="p-4 text-center text-slate-400 text-sm">No one on the waitlist.</div>
                    ) : rsvpData.waitlist.filter(w => ['waiting', 'offered'].includes(w.status)).map((w, i) => (
                      <div key={w.waitlist_id} className="flex items-center justify-between p-3" data-testid={`waitlist-item-${w.waitlist_id}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400 w-5">{i + 1}.</span>
                          <div>
                            <p className="font-medium text-slate-800 text-sm">{w.name}</p>
                            <p className="text-xs text-slate-500">{w.email} · {w.party_size} place(s)</p>
                          </div>
                          <Badge variant={w.status === 'offered' ? 'default' : 'secondary'} className="text-[10px] ml-1">{w.status}</Badge>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => removeWaitlist(w.waitlist_id)} data-testid={`waitlist-remove-${w.waitlist_id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Events;
